import React from 'react';
import type { ConflictingMigration } from '../../types';
import { getScheduleDisplay } from '../../utils/cron';
import { ui } from '../../config/uiSizes';

interface ConflictingMigrationsListProps {
    conflicts: ConflictingMigration[];
    onViewDetails: (migrationId: string) => void;
    /** Resolves a stored org name/key (e.g. "edcieast") to its friendly display name (e.g. "EDCi East Region"). */
    resolveOrgName: (storedName: string) => string;
}

interface ConflictSectionProps {
    title: string;
    items: ConflictingMigration[];
    renderRoute: (c: ConflictingMigration) => React.ReactNode;
    onViewDetails: (migrationId: string) => void;
}

const ConflictSection: React.FC<ConflictSectionProps> = ({ title, items, renderRoute, onViewDetails }) => (
    <div>
        <p className={`${ui.text.xs} font-semibold uppercase tracking-wide text-yellow-700/80 dark:text-yellow-300/80 mb-2`}>
            {title}
        </p>
        <ul className={`flex flex-col ${ui.gap.sm}`}>
            {items.map((c) => {
                const schedule = getScheduleDisplay(c.schedule);
                return (
                    <li
                        key={c.id}
                        className={`flex items-start justify-between ${ui.gap.sm} rounded-md border border-yellow-300/70 dark:border-yellow-600/50 bg-yellow-100/50 dark:bg-yellow-800/20 ${ui.page.smPadding}`}
                    >
                        <div className="min-w-0">
                            <p className={`${ui.text.label} font-semibold text-yellow-900 dark:text-yellow-100`}>
                                {c.name}
                            </p>
                            <div className={`flex flex-wrap items-center ${ui.gap.sm} mt-0.5`}>
                                <span className={`${ui.text.xs} text-yellow-700 dark:text-yellow-300`}>
                                    {renderRoute(c)}
                                </span>
                                {schedule && (
                                    <span className={`inline-flex items-center rounded-full bg-yellow-200/70 dark:bg-yellow-700/40 ${ui.badge.padding} ${ui.text.xs} font-medium text-yellow-800 dark:text-yellow-200`}>
                                        {schedule}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => onViewDetails(c.id)}
                            className={`shrink-0 rounded-md border border-yellow-400 dark:border-yellow-500 bg-yellow-200/60 dark:bg-yellow-700/40 hover:bg-yellow-200 dark:hover:bg-yellow-700/60 font-medium text-yellow-900 dark:text-yellow-100 ${ui.button.sm} focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                        >
                            View details
                        </button>
                    </li>
                );
            })}
        </ul>
    </div>
);

const ConflictingMigrationsList: React.FC<ConflictingMigrationsListProps> = ({ conflicts, onViewDetails, resolveOrgName }) => {
    const orgToOrg = conflicts.filter((c) => c.migrationType === 'OrgToOrg');
    const d2d = conflicts.filter((c) => c.migrationType === 'DivisionToDivision');

    return (
        <div className={`flex flex-col ${ui.gap.lg} mt-3`}>
            {orgToOrg.length > 0 && (
                <ConflictSection
                    title="Org-to-Org"
                    items={orgToOrg}
                    renderRoute={(c) => `${resolveOrgName(c.sourceOrgName)} → ${resolveOrgName(c.targetOrgName)}`}
                    onViewDetails={onViewDetails}
                />
            )}
            {d2d.length > 0 && (
                <ConflictSection
                    title="Division-to-Division"
                    items={d2d}
                    renderRoute={(c) => `${c.sourceDivisionName} → ${c.targetDivisionName}`}
                    onViewDetails={onViewDetails}
                />
            )}
        </div>
    );
};

export default ConflictingMigrationsList;
